import { type JSX, useEffect, useId, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { steuerschluessel as steuerschluesselLaden, type Steuerschluessel } from '../api/produkte';
import { freieGutschriftAnlegen, gutschriftAnlegen, type Rechnung } from '../api/rechnungen';
import { alsEuro, alsMenge, alsProzent } from '../betrag';
import { heute } from '../datum';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { Dialog } from '../komponenten/Dialog';
import { Meldung } from '../komponenten/Meldung';

/**
 * RNG-11: die Gutschrift — mit Bezug aus der Rechnungsakte, ohne Bezug
 * (frei, etwa aus Kulanz) aus der Kundenakte. Auftrag CRM-114, Lücke L4.
 *
 * Beide Pop-ups legen einen ENTWURF an: Er hat keine Nummer und lässt sich
 * verwerfen. Deshalb fragen sie nicht nach (Tabelle 29, ÄA-62 B) — die
 * Rückfrage steht vor dem Festschreiben in der Akte, das die Nummer zieht und
 * den Beleg unveränderlich macht (RNG-03, RNG-04).
 *
 * Einspaltig ab 360 px (ÄA-63): Die Positionen stehen als Liste untereinander,
 * nicht als Tabelle.
 */

/** Deutsche Eingabe („12,50") in die Form der API („12.50"), wie im Zahlungsformular. */
function alsApiZahl(eingabe: string): string {
  return eingabe.trim().replace(',', '.');
}

function mengeAlsEingabe(menge: string | null): string {
  return menge === null ? '' : String(Number(menge)).replace('.', ',');
}

export function GutschriftZurRechnung({
  rechnung,
  beiSchliessen,
  beiAngelegt,
}: {
  readonly rechnung: Rechnung;
  readonly beiSchliessen: () => void;
  readonly beiAngelegt: (id: string) => void;
}): JSX.Element {
  const kennung = useId();
  // Gutgeschrieben werden Zeilen mit Menge und Preis (wie in der API).
  const zeilen = rechnung.positionen.filter(
    (p) => (p.art === 'artikel' || p.art === 'freitext') && p.menge !== null && p.einzelpreis !== null,
  );
  const gruppen = rechnung.summen.gruppen;
  const [gewaehlt, setzeGewaehlt] = useState<Readonly<Record<string, string | undefined>>>({});
  const [betrag, setzeBetrag] = useState('');
  const [schluessel, setzeSchluessel] = useState(gruppen[0]?.schluessel ?? '');
  const [grund, setzeGrund] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | undefined>(undefined);

  const auswahl = Object.entries(gewaehlt).filter(
    (e): e is [string, string] => e[1] !== undefined,
  );
  const vollstaendig = grund.trim() !== '' && (auswahl.length > 0 || betrag.trim() !== '');

  async function anlegen(): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const neu = await gutschriftAnlegen(rechnung.id, {
        grund: grund.trim(),
        zeilen: auswahl.map(([position, menge]) => ({ position, menge: alsApiZahl(menge) })),
        betrag: betrag.trim() === ''
          ? null
          : { brutto: alsApiZahl(betrag), steuerschluessel: schluessel },
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler | Error);
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog
      titel="Gutschrift"
      unterzeile={`Zu ${rechnung.rechnungsnummer ?? ''} über ${alsEuro(rechnung.summen.belegbrutto)}`}
      beiSchliessen={() => { if (!laeuft) beiSchliessen(); }}
    >
      <form
        className="formular"
        onSubmit={(e) => { e.preventDefault(); if (vollstaendig && !laeuft) void anlegen(); }}
      >
        {zeilen.length > 0 && (
          <fieldset className="feld">
            <legend className="feld__beschriftung">Positionen gutschreiben</legend>
            <ul className="gutschrift__zeilen">
              {zeilen.map((p) => {
                const menge = gewaehlt[p.id];
                return (
                  <li key={p.id} className="gutschrift__zeile">
                    <label className="feld--wahl">
                      <input
                        type="checkbox"
                        checked={menge !== undefined}
                        onChange={(e) =>
                          setzeGewaehlt({
                            ...gewaehlt,
                            [p.id]: e.target.checked ? mengeAlsEingabe(p.menge) : undefined,
                          })}
                      />{' '}
                      {p.bezeichnung}
                    </label>
                    <span className="feld__hilfe">
                      berechnet {alsMenge(p.menge)} {p.mengeneinheit ?? ''} zu{' '}
                      {alsEuro(p.einzelpreis)}, {alsProzent(p.steuerProzent)} %
                    </span>
                    {menge !== undefined && (
                      <div className="feld">
                        <label className="feld__beschriftung" htmlFor={`${kennung}-${p.id}`}>
                          Menge gutschreiben
                        </label>
                        <input
                          id={`${kennung}-${p.id}`}
                          inputMode="decimal"
                          value={menge}
                          onChange={(e) => setzeGewaehlt({ ...gewaehlt, [p.id]: e.target.value })}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </fieldset>
        )}

        <fieldset className="feld">
          <legend className="feld__beschriftung">Oder ein Betrag</legend>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`${kennung}-betrag`}>
              Betrag brutto
            </label>
            <input
              id={`${kennung}-betrag`}
              inputMode="decimal"
              value={betrag}
              onChange={(e) => setzeBetrag(e.target.value)}
            />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor={`${kennung}-satz`}>
              Steuersatz
            </label>
            {/* BP-31: Die Minderung gehört zu einem Steuersatz, den die Rechnung auswies. */}
            <select
              id={`${kennung}-satz`}
              value={schluessel}
              onChange={(e) => setzeSchluessel(e.target.value)}
            >
              {gruppen.map((g) => (
                <option key={g.schluessel} value={g.schluessel}>
                  {alsProzent(g.prozent)} % ({g.schluessel}), auf der Rechnung {alsEuro(g.brutto)}
                </option>
              ))}
            </select>
          </div>
        </fieldset>

        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-grund`}>
            Grund der Gutschrift
          </label>
          <textarea
            id={`${kennung}-grund`}
            rows={2}
            value={grund}
            onChange={(e) => setzeGrund(e.target.value)}
          />
          <span className="feld__hilfe">Er steht auf der Gutschrift.</span>
        </div>

        <p className="feld__hilfe">
          Es entsteht ein <strong>Entwurf</strong> mit Bezug auf diese Rechnung. Die Nummer
          kommt beim Festschreiben; erst dann mindert die Gutschrift den offenen Posten.
        </p>
        {fehler instanceof ApiFehler && <Meldung fehler={fehler} />}
        {fehler !== undefined && !(fehler instanceof ApiFehler) && (
          <p className="meldung meldung--fehler" role="alert">{fehler.message}</p>
        )}
        <Abschlussreihe
          abbruch={<button type="button" className="knopf" disabled={laeuft} onClick={beiSchliessen}>Abbrechen</button>}
        >
          <button type="submit" className="knopf knopf--haupt" disabled={!vollstaendig || laeuft}>
            Gutschrift anlegen
          </button>
        </Abschlussreihe>
      </form>
    </Dialog>
  );
}

export function FreieGutschrift({
  kunde,
  kundenname,
  beiSchliessen,
  beiAngelegt,
}: {
  readonly kunde: string;
  readonly kundenname: string;
  readonly beiSchliessen: () => void;
  readonly beiAngelegt: (id: string) => void;
}): JSX.Element {
  const kennung = useId();
  const [saetze, setzeSaetze] = useState<readonly Steuerschluessel[] | undefined>(undefined);
  const [schluessel, setzeSchluessel] = useState('');
  const [betrag, setzeBetrag] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [grund, setzeGrund] = useState('');
  const [von, setzeVon] = useState(heute());
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | Error | undefined>(undefined);

  useEffect(() => {
    let aktiv = true;
    steuerschluesselLaden().then(
      (liste) => {
        if (!aktiv) return;
        setzeSaetze(liste);
        // Vorbelegt mit dem höchsten Satz — dem Regelsatz.
        const regel = [...liste].sort((a, b) => Number(b.prozent) - Number(a.prozent))[0];
        setzeSchluessel(regel?.schluessel ?? '');
      },
      () => { if (aktiv) setzeSaetze([]); },
    );
    return () => { aktiv = false; };
  }, []);

  const vollstaendig = grund.trim() !== '' && betrag.trim() !== '' && schluessel.trim() !== ''
    && von !== '';

  async function anlegen(): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const neu = await freieGutschriftAnlegen({
        kunde,
        grund: grund.trim(),
        bezeichnung: bezeichnung.trim(),
        brutto: alsApiZahl(betrag),
        steuerschluessel: schluessel.trim(),
        leistungVon: von,
        leistungBis: null,
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler | Error);
      setzeLaeuft(false);
    }
  }

  return (
    <Dialog
      titel="Freie Gutschrift"
      unterzeile={`An ${kundenname}, ohne Bezug auf eine Rechnung`}
      breite="schmal"
      beiSchliessen={() => { if (!laeuft) beiSchliessen(); }}
    >
      <form
        className="formular"
        onSubmit={(e) => { e.preventDefault(); if (vollstaendig && !laeuft) void anlegen(); }}
      >
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-grund`}>
            Grund der Gutschrift
          </label>
          <textarea
            id={`${kennung}-grund`}
            rows={2}
            value={grund}
            onChange={(e) => setzeGrund(e.target.value)}
          />
          <span className="feld__hilfe">Etwa Kulanz; er steht auf der Gutschrift.</span>
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-bezeichnung`}>
            Bezeichnung der Zeile (wahlweise)
          </label>
          <input
            id={`${kennung}-bezeichnung`}
            value={bezeichnung}
            onChange={(e) => setzeBezeichnung(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-betrag`}>
            Betrag brutto
          </label>
          <input
            id={`${kennung}-betrag`}
            inputMode="decimal"
            value={betrag}
            onChange={(e) => setzeBetrag(e.target.value)}
          />
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-satz`}>
            Steuersatz
          </label>
          {saetze !== undefined && saetze.length > 0 ? (
            <select
              id={`${kennung}-satz`}
              value={schluessel}
              onChange={(e) => setzeSchluessel(e.target.value)}
            >
              {saetze.map((s) => (
                <option key={s.schluessel} value={s.schluessel}>
                  {alsProzent(s.prozent)} % — {s.bezeichnung} ({s.schluessel})
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`${kennung}-satz`}
              placeholder="DE-19"
              value={schluessel}
              onChange={(e) => setzeSchluessel(e.target.value)}
            />
          )}
        </div>
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`${kennung}-von`}>
            Leistungsdatum
          </label>
          {/* § 14 Abs. 4 Nr. 6, BP-33: auch die Gutschrift nennt den Zeitpunkt. */}
          <input
            id={`${kennung}-von`}
            type="date"
            value={von}
            onChange={(e) => setzeVon(e.target.value)}
          />
        </div>
        <p className="feld__hilfe">
          Es entsteht ein <strong>Entwurf</strong>. Nach dem Festschreiben steht der Betrag als
          Guthaben des Kunden, bis er erstattet oder verrechnet ist.
        </p>
        {fehler instanceof ApiFehler && <Meldung fehler={fehler} />}
        {fehler !== undefined && !(fehler instanceof ApiFehler) && (
          <p className="meldung meldung--fehler" role="alert">{fehler.message}</p>
        )}
        <Abschlussreihe
          abbruch={<button type="button" className="knopf" disabled={laeuft} onClick={beiSchliessen}>Abbrechen</button>}
        >
          <button type="submit" className="knopf knopf--haupt" disabled={!vollstaendig || laeuft}>
            Gutschrift anlegen
          </button>
        </Abschlussreihe>
      </form>
    </Dialog>
  );
}
